import { useState } from "react";
import { toast } from "sonner";
import { AccountType, detectBank } from "@boo/shared";
import type { AccountSuggestion } from "@boo/shared";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ApiError } from "@/services/expenseApi";
import { useAccountStore } from "../store/useAccountStore";
import AccountAvatar from "./AccountAvatar";
import { ACCOUNT_TYPE_LABELS, INPUT_CLASS } from "../types/accounts.types";

const ACCOUNT_TYPES = Object.values(AccountType);

export default function SuggestionCard({
  suggestion,
}: {
  suggestion: AccountSuggestion;
}) {
  const { acceptSuggestion, dismissSuggestion } = useAccountStore();
  const label = suggestion.institution ?? "Account";
  const [name, setName] = useState(`${label} ••${suggestion.digits}`);
  const [type, setType] = useState<AccountType>(suggestion.type);
  const [institution, setInstitution] = useState(suggestion.institution ?? "");
  const [busy, setBusy] = useState(false);

  async function run(action: () => Promise<void>) {
    setBusy(true);
    try {
      await action();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Action failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="border-primary/40">
      <CardContent className="pt-4 flex flex-col gap-3">
        <div className="flex items-center gap-3 text-sm text-foreground">
          <AccountAvatar
            type={type}
            bankCode={detectBank(suggestion.institution)}
          />
          <div>
            <span className="font-medium">
              {label} ••{suggestion.digits}
            </span>{" "}
            <span className="text-muted-foreground">
              {suggestion.fromStatement
                ? "appeared on an imported statement"
                : `appeared on ${suggestion.evidenceCount} recent slips`}
              {". Add it as an account?"}
            </span>
          </div>
        </div>
        <div className="grid gap-2 sm:grid-cols-3">
          <input
            className={`${INPUT_CLASS} py-2`}
            value={name}
            maxLength={100}
            onChange={(e) => setName(e.target.value)}
            aria-label="Account name"
          />
          <select
            className={`${INPUT_CLASS} py-2`}
            value={type}
            onChange={(e) => setType(e.target.value as AccountType)}
            aria-label="Account type"
          >
            {ACCOUNT_TYPES.map((t) => (
              <option key={t} value={t}>
                {ACCOUNT_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
          <input
            className={`${INPUT_CLASS} py-2`}
            value={institution}
            placeholder="Institution"
            maxLength={100}
            onChange={(e) => setInstitution(e.target.value)}
            aria-label="Institution"
          />
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={() => run(() => dismissSuggestion(suggestion.id, true))}
          >
            Never
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => run(() => dismissSuggestion(suggestion.id, false))}
          >
            Not now
          </Button>
          <Button
            size="sm"
            disabled={busy || !name.trim()}
            loading={busy}
            onClick={() =>
              run(() =>
                acceptSuggestion(suggestion.id, {
                  name: name.trim(),
                  type,
                  institution: institution.trim() || undefined,
                  digits: suggestion.digits,
                }),
              )
            }
          >
            Add account
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
